import React, { useRef, useEffect, useState } from 'react';
import { useFontLoader } from '../../../../dummies/RichTextEditor/useFontLoader';
import { heritageColors } from '../../../../heritageTheme.ts';

const Divider = ({ color }) => (
  <li aria-hidden="true" style={{
    display: 'flex',
    alignItems: 'center',
    gap: '0.4em',
    padding: '0.1em 0',
    userSelect: 'none',
  }}>
    <span style={{ flex: 1, height: '1px', backgroundColor: color, opacity: 0.4 }} />
    <span style={{ color, fontSize: '0.55em', opacity: 0.7, lineHeight: 1 }}>✦</span>
    <span style={{ flex: 1, height: '1px', backgroundColor: color, opacity: 0.4 }} />
  </li>
);

const Ornament = ({ data = { notices: [] }, title = 'לוח מודעות', showTitle = true }) => {
  const contentRef = useRef(null);
  const containerRef = useRef(null);
  const firstSetRef = useRef(null);
  const [needsScroll, setNeedsScroll] = useState(false);
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(id);
  }, []);

  const activeNotices = (data.notices || []).filter(
    n => !n.expiryDate || new Date(n.expiryDate) > now
  );

  const allNoticesHtml = activeNotices.map(n => n.content).join(' ') || '';
  useFontLoader(allNoticesHtml);

  useEffect(() => {
    const checkScroll = () => {
      if (!containerRef.current || !firstSetRef.current || activeNotices.length === 0) return;
      const containerHeight = containerRef.current.clientHeight;
      const contentHeight = firstSetRef.current.offsetHeight;
      setNeedsScroll(containerHeight > 0 && contentHeight > containerHeight + 2);
    };
    const timer = setTimeout(checkScroll, 100);
    const resizeObserver = new ResizeObserver(checkScroll);
    if (containerRef.current) resizeObserver.observe(containerRef.current);
    return () => { clearTimeout(timer); resizeObserver.disconnect(); };
  }, [activeNotices]);

  const accentColor = heritageColors.gold || '#D4AF37';
  const navyColor = heritageColors.navy || '#1a3a5c';
  const borderColor = heritageColors.border || '#D7CCC8';

  const renderItems = (notices, keyPrefix = '') => {
    const items = [];
    notices.forEach((notice, i) => {
      items.push(
        <li key={`${keyPrefix}${notice.id}`} data-notice-item style={{ padding: '0.55em 0' }}>
          <div
            style={{ fontSize: '0.78em', color: navyColor, lineHeight: 1.6, width: '100%' }}
            dangerouslySetInnerHTML={{ __html: notice.content }}
          />
        </li>
      );
      if (i < notices.length - 1) {
        items.push(<Divider key={`${keyPrefix}div-${notice.id}`} color={accentColor} />);
      }
    });
    return items;
  };

  return (
    <div style={{
      padding: '20px',
      direction: 'rtl',
      height: '100%',
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      fontSize: 'inherit',
      boxSizing: 'border-box',
    }}>
      {showTitle && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          borderBottom: `0.0625em solid ${borderColor}`,
          paddingBottom: '8px',
          marginBottom: '8px',
        }}>
          <span className="material-symbols-outlined" style={{ color: navyColor, fontSize: '1.1em' }}>campaign</span>
          <h3 style={{ margin: 0, fontWeight: 'bold', color: navyColor, fontSize: '0.875em' }}>{title}</h3>
        </div>
      )}

      {activeNotices.length === 0 ? (
        <p style={{ color: heritageColors.slate || '#795548', fontSize: '0.875em' }}>אין מודעות להצגה</p>
      ) : (
        <div ref={containerRef} style={{ overflow: 'hidden', flex: 1, position: 'relative' }}>
          {needsScroll && (
            <style>{`
              @keyframes scrollNoticesOrnament {
                0% { transform: translateY(0); }
                100% { transform: translateY(calc(-100% / 3)); }
              }
            `}</style>
          )}
          <div ref={contentRef} style={{
            animation: needsScroll ? 'scrollNoticesOrnament 30s linear infinite' : 'none',
          }}>
            <ul ref={firstSetRef} style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {renderItems(activeNotices)}
            </ul>
            {needsScroll && (
              <>
                {/* bridge divider between repeated sets */}
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  <Divider color={accentColor} />
                  {renderItems(activeNotices, 'dup1-')}
                </ul>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  <Divider color={accentColor} />
                  {renderItems(activeNotices, 'dup2-')}
                </ul>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Ornament;
